import React, { useState, useCallback, useEffect } from 'react';
import Modal from '@sabre/spark-react-core/modal';
import { array, bool, string } from 'prop-types';
import {
   URL_CORE_PRICING_SAVE_DISTRIBUTION,
   URL_NEW_DISTRIBUTION_GROUP_GENERATE,
   URL_NEW_DISTRIBUTION_GROUP_SEARCH,
   URL_DISTRIBUTION_GROUP_APPROVED,
} from '../../end-points.js';
import { useCreateRecord } from '../../hooks/use-create-record.js';
import { useDistribute } from '../../hooks/use-distribute.js';
import { useFetch } from '../../hooks/use-fetch.js';
import { useSave } from '../../hooks/use-save.js';
import AgGridReactTable from '../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import DistributionDeleteConfirmation from '../../ui-components/core-pricing-component/distribution-delete-confirmation-modal/distribution-delete-confirmation-modal.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import { createUniqueId, getHostCarrierCode } from '../../utils/common-util.js';
import {
   SIZE,
   APPROVAL_NEEDED_EDIT_FIELD,
   API_STATUS,
   LAST_SAVED_TABLE_TYPE,
} from '../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages.js';
import { toastSuccess, toastWarn } from '../../utils/toasts.js';
import {
   findSelectedRecordDetails,
   updateRowDatWithNotes,
   updateDistributionGroupLogData,
   insertUniqueId,
   getContextMenuConfig,
   filterDistributionGroupNumbers,
   removedSelectedRecord,
   updateDistributionGroupRecords,
   parseApprovedPayload,
   parseSavePayload,
   removeIntDistributionTimeStamp,
   findLastEditedDetails,
   generateEditRecordsDetails,
   insertNewRecord,
} from './__helpers__/functions.js';
import {
   DISTRIBUTION_LIST_TABLE_HEADERS,
   DISTRIBUTION_LIST_TABLE_LOG_HEADER,
} from './__helpers__/tableHeaders.js';
import DistributionNote from './distribution-note.jsx';

function DistributionListTable({
   rowData: rowDataFromParent = [],
   isLoading = false,
   selectedScreen,
}) {
   const [rowData, setRowData] = useState([]);
   const customContextMenuConfig = getContextMenuConfig(rowData);
   const totalRowCount = rowData.length;
   const [selectedRows, setSelectedRows] = useState(['0']);
   const [logTable, setLogTable] = useState([]);
   const [isApproveAllowed, setIsApproveAllowed] = useState(false);
   const [lastEditedFields, setLastEditedFields] = useState([]);
   const [gridApi, setGridApi]= useState({});
   const [showDeleteConformationPopup, setShowDeleteConformationPopup] =
      useState(false);
   const [lastSavedTable, setLastSavedTable] = useState('');
   const [deleteDistributionGroupRecords, setDeleteDistributionGroupRecord] =
      useState({});
   const { saveResponseData, saveStatus, saveData } = useSave({});
   const {createResponseData, createStatus, createApi } = useCreateRecord({});
   const { distributeResponseData, distributeStatus, distributeApi } =
      useDistribute({});
   const { fetchResponseData, fetchStatus, fetchApi } = useFetch();
   const rowSelectionIndicator =
      selectedRows.length > 1
         ? `${selectedRows.length} records selected`
         : `Record ${selectedRows[0]} selected`;

   const updateSelectedRows = useCallback((selectedNodes) => {
      const selectedRowIndices = selectedNodes?.map(
         (node) => node.rowIndex + 1
      );
      setSelectedRows(selectedRowIndices.length ? selectedRowIndices : ['0']);
      const logTableRowData =
         selectedNodes.length > 1
            ? []
            : selectedNodes[0]?.data?.distributionGroupLogs;
      setLogTable(logTableRowData);
   }, []);

   useEffect(() => {
      if (saveResponseData?.length > 0 && saveStatus === API_STATUS.SUCCESS) {
         if (lastSavedTable === LAST_SAVED_TABLE_TYPE.MAIN_TABLE) {
            const updatedDistributionGroupData = updateDistributionGroupRecords(
               saveResponseData,
               rowData
            );
            setRowData(updatedDistributionGroupData);
            setLastEditedFields([]);
            if (isApproveAllowed) {
               const approvePayload = parseApprovedPayload(saveResponseData);
               approveDistributionGroup(approvePayload);
            }
         } else if (lastSavedTable === LAST_SAVED_TABLE_TYPE.LOG_TABLE) {
            const updateRowData = updateDistributionGroupLogData(
               rowData,
               saveResponseData
            );
            const selectedRowDetails = findSelectedRecordDetails(
               selectedRows,
               updateRowData
            );
            setRowData(updateRowData);
            setLogTable(selectedRowDetails.distributionGroupLogs);
         }
         toastSuccess(NOTIFICATION_MESSAGES.DISTRIBUTION_SAVED_SUCCESS);
      }
   }, [saveResponseData, saveStatus]);

   useEffect(() => {
      if (
         fetchResponseData?.length > 0 &&
         fetchStatus === API_STATUS.COMPLETED
      ) {
         const updatedRecord = updateDistributionGroupRecords(
            fetchResponseData,
            rowData
         );
         setRowData(updatedRecord);
      }
   }, [fetchResponseData, fetchStatus]);

   useEffect(() => {
      if (
         distributeResponseData?.length > 0 &&
         distributeStatus === API_STATUS.COMPLETED
      ) {
         const updatedDistributionGroupData = updateDistributionGroupRecords(
            distributeResponseData,
            rowData
         );
         setRowData(updatedDistributionGroupData);
         setIsApproveAllowed(false);
         toastSuccess(NOTIFICATION_MESSAGES.DISTRIBUTION_APPROVED_SUCCESS);
      } else if (distributeStatus === API_STATUS.FAILED)
         setIsApproveAllowed(false);
   }, [distributeResponseData, distributeStatus]);

   const approveDistributionGroup = (approvePayload) => {
      distributeApi(approvePayload, URL_DISTRIBUTION_GROUP_APPROVED);
   };

   const onCreateNewDistributionGroupNumber =  (gridApiRef) => {
     createApi(
         URL_NEW_DISTRIBUTION_GROUP_GENERATE
      );
      setGridApi(gridApiRef);
   };

   useEffect(()=>{
      if (
         createResponseData?.dgNumber &&
         createStatus === API_STATUS.COMPLETED
      ) {
         const updatedDistributionGroupData = insertNewRecord(
            createResponseData,
            rowData
         );
         setRowData(updatedDistributionGroupData);
         setLogTable(createResponseData?.distributionGroupLogs);
             gridApi?.current?.deselectAll();
         const newRowNode = gridApi?.current?.getRowNode(updatedDistributionGroupData?.id);
         newRowNode?.setSelected(true);
         setGridApi({});
         toastSuccess(NOTIFICATION_MESSAGES.NEW_DISTRIBUTION_GENERATE_SUCCESS);
      }
   },[createResponseData, createStatus]);

   const onDeleteChanges = (rowsToDelete) => {
      const deletedDistributionGroupNumbers =
         filterDistributionGroupNumbers(rowsToDelete);
      const payload = {
         dgNumber: deletedDistributionGroupNumbers,
         hostCarrier: getHostCarrierCode(),
      };
      setDeleteDistributionGroupRecord(payload);
      setShowDeleteConformationPopup(true);
   };

   const onRemoveSelectedRows = (rowsToRemove) => {
      const removeDistributionGroupNumbers =
         filterDistributionGroupNumbers(rowsToRemove);
      const updateRowData = removedSelectedRecord(
         removeDistributionGroupNumbers,
         rowData
      );
      setRowData(updateRowData);
      toastSuccess(NOTIFICATION_MESSAGES.DISTRIBUTION_REMOVED_SUCCESS);
   };

   const onUndoRows = (rowsToUndo) => {
      if (rowsToUndo.length > 0) {
         const undoDistributionGroupNumbers =
            filterDistributionGroupNumbers(rowsToUndo);
         fetchApi(
            [
               {
                  key: 'dgNumber',
                  operator: '==',
                  value: undoDistributionGroupNumbers.toString(),
               },
            ],
            URL_NEW_DISTRIBUTION_GROUP_SEARCH
         );
      }
   };

   const onSaveChanges = (editedData) => {
      const updatedRecord = parseSavePayload(editedData, lastEditedFields);
      saveData(updatedRecord, URL_CORE_PRICING_SAVE_DISTRIBUTION, 'put');
      setLastSavedTable(LAST_SAVED_TABLE_TYPE.MAIN_TABLE);
   };

   const onOpenNewTab = (selectedRecord) => {
      const selectedDistributionGroupNumbers =
         filterDistributionGroupNumbers(selectedRecord);
      sessionStorage.setItem(
         'distributionNumbers',
         selectedDistributionGroupNumbers.toString()
      );
      window.open('/workflow/fo-core-pricing', '');
   };

   const onDistribute = (selectedRecord) => {
      const approveRecord = parseApprovedPayload(selectedRecord);
      const payload = removeIntDistributionTimeStamp(approveRecord);
      approveDistributionGroup(payload);
   };

   const handleDeleteApiCallBack = (deletedRecord) => {
      const updatedRowData = rowData.filter(
         (record) => !deletedRecord.includes(record.dgNumber)
      );
      setRowData(updatedRowData);
      setShowDeleteConformationPopup(false);
      toastSuccess(NOTIFICATION_MESSAGES.DISTRIBUTION_DELETE_SUCCESS);
   };

   const handleDeletePopupClose = () => {
      setShowDeleteConformationPopup(false);
   };

   const onNoteSendClick = (noteTextValue) => {
      const selectedRowDetails = findSelectedRecordDetails(
         selectedRows,
         rowData
      );
      const payload = updateRowDatWithNotes(selectedRowDetails, noteTextValue);
      saveData([payload], URL_CORE_PRICING_SAVE_DISTRIBUTION, 'put');
      setLastSavedTable(LAST_SAVED_TABLE_TYPE.LOG_TABLE);
   };

   const onSelectionValueChanged = (editedColumnName, editedStatus) => {
      const editedFieldName = findLastEditedDetails(
         DISTRIBUTION_LIST_TABLE_HEADERS,
         editedColumnName
      );
      const selectedRowDetails = findSelectedRecordDetails(
         selectedRows,
         rowData
      );
      const editedFieldsRecords = generateEditRecordsDetails(
         selectedRowDetails?.id,
         lastEditedFields,
         editedFieldName
      );
      setLastEditedFields(editedFieldsRecords);
      if (
         APPROVAL_NEEDED_EDIT_FIELD.includes(editedColumnName) &&
         editedStatus
      ) {
         setIsApproveAllowed(true);
      }
   };

   useEffect(() => {
      if (rowDataFromParent?.length > 0) {
         const updatedData = insertUniqueId(rowDataFromParent);
         if (updatedData?.length > 0) {
            setRowData(updatedData);
            setLogTable(updatedData[0]?.distributionGroupLogs);
         }
      }
   }, [rowDataFromParent]);

   return (
      <section className='core-pricing-distribution-list'>
         {showDeleteConformationPopup && (
            <div className='distribution-delete-popup'>
               <Modal
                  onClose={handleDeletePopupClose}
                  open={showDeleteConformationPopup}
                  titleHeadingLevel={3}
                  modalId='dashboard-search-modal'
                  className={`dashboard-search-modal${
                     showDeleteConformationPopup ? '' : ' spark-hidden'
                  } `}
                  contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
               >
                  <DistributionDeleteConfirmation
                     distributionListToDelete={deleteDistributionGroupRecords}
                     onUpdateDistributionTable={handleDeleteApiCallBack}
                     onCancelButtonClick={handleDeletePopupClose}
                     selectedScreen={selectedScreen}
                  />
               </Modal>
            </div>
         )}
         <div className='spark-flex core-pricing-distribution-list-table'>
            {isLoading ? (
               <div className='loader'>
                  <Loader size={SIZE.SMALL} />
               </div>
            ) : (
               <>
                  <div className='spark-flex col-lg-12 col-md-12 col-sm-12 spark-pad-0'>
                     <div className='col-lg-8 col-md-8 col-sm-8 spark-pad-0'>
                        <AgGridReactTable
                           rowData={rowData}
                           headers={DISTRIBUTION_LIST_TABLE_HEADERS}
                           enableMultiRowSelection
                           enableRowIds
                           onSelectedRowsChange={updateSelectedRows}
                           onSelectionValueChanged={onSelectionValueChanged}
                           contextMenuConfig={{
                              ...customContextMenuConfig,
                              onInsertNewRowWithData:
                                 onCreateNewDistributionGroupNumber,
                              onDeleteChanges,
                              onRemoveSelectedRows,
                              onUndoRows,
                              onSaveChanges,
                              onOpenNewTab,
                              onDistribute,
                           }}
                        />
                     </div>
                     <div className='col-lg-4 col-md-4 col-sm-4 spark-pad-0 core-pricing-distribution-list-log-table'>
                        <AgGridReactTable
                           rowData={selectedRows.length === 1 ? logTable : []}
                           headers={DISTRIBUTION_LIST_TABLE_LOG_HEADER}
                        />
                        <div>
                           <DistributionNote
                              onNoteSendClick={onNoteSendClick}
                              showWarning={toastWarn}
                              disabled={
                                 rowData.length === 0 || selectedRows.length > 1
                              }
                           />
                        </div>
                     </div>
                  </div>
                  <p className='spark-bold spark-flex justify-between spark-pad-t-.5 row-selection-info'>
                     {rowSelectionIndicator} (Total {totalRowCount})
                  </p>
               </>
            )}
         </div>
      </section>
   );
}

DistributionListTable.propTypes = {
   rowData: array,
   selectedScreen: string,
   isLoading: bool,
};

export default DistributionListTable;
